/**
 * Kart temsili.
 *
 * Başgan'da renk oyun içinde tamamen önemsizdi; BURADA TAM TERSİ. Üç Beş Sekiz
 * bir koz (batak) oyunu: renk takibi zorunlu, koz her rengi keser. Kartın
 * kimliği hem rütbesi hem rengi.
 *
 * Rütbe sıralaması klasik batak: 2 < 3 < … < 10 < J < Q < K < A
 * Sayısal karşılığı 2..14 — yani "2" en DÜŞÜK kart. (Başgan'da en yüksekti;
 * burada karo 2 yalnızca maçın ilk 8'cisini belirleyen bir işaret.)
 */

export type Suit = 'S' | 'H' | 'D' | 'C'; // maça, kupa, karo, sinek
export const SUITS: readonly Suit[] = ['S', 'H', 'D', 'C'];

/**
 * Elde gösterim sırası: KARO, SİNEK, KUPA, MAÇA. `SUITS`'ten ayrı çünkü o
 * sıra kart kimliğinin ikili gösteriminden geliyor.
 *
 * Sıra SABİT — koz da dahil hiçbir şey onu değiştirmiyor. Koz bloğunu öne
 * almak denendi ve bırakıldı: koz her el değiştiği için kartlar el altında
 * yer değiştiriyor, alışverişten sonra elin dizilişi bambaşka oluyor ve
 * oyuncu bildiği yere basıp yanlış kartı atıyordu. Sabit sıra, kas hafızası
 * demek; koz zaten kartın kendi çerçevesinden belli.
 */
export const DISPLAY_SUITS: readonly Suit[] = ['D', 'C', 'H', 'S'];

export const SUIT_NAMES: Record<Suit, string> = {
  S: 'Maça',
  H: 'Kupa',
  D: 'Karo',
  C: 'Sinek',
};

export const SUIT_GLYPHS: Record<Suit, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };

export const isRed = (suit: Suit): boolean => suit === 'H' || suit === 'D';

export type Rank = 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14;
export const RANKS: readonly Rank[] = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

/** 0..51 arası kompakt kart kimliği. rank = 2 + (id >> 2), suit = SUITS[id & 3] */
export type CardId = number;

export const rankOf = (c: CardId): Rank => (2 + (c >> 2)) as Rank;
export const suitOf = (c: CardId): Suit => SUITS[c & 3]!;
export const cardOf = (rank: Rank, suit: Suit): CardId =>
  ((rank - 2) << 2) | SUITS.indexOf(suit);

/**
 * Maçın İLK elinde 8'ciyi belirleyen kart. Yalnızca belirleyici — oyun içinde
 * hiçbir özel gücü yok, sonraki ellerde roller sırayla dönüyor.
 */
export const KARO_TWO: CardId = cardOf(2, 'D');

const RANK_LABELS: Record<number, string> = { 11: 'J', 12: 'Q', 13: 'K', 14: 'A' };
const LABEL_RANKS: Record<string, Rank> = {
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
  '10': 10, J: 11, Q: 12, K: 13, A: 14,
};

export const rankLabel = (r: Rank): string => RANK_LABELS[r] ?? String(r);
export const cardToString = (c: CardId): string => rankLabel(rankOf(c)) + suitOf(c);
export const cardsToString = (cs: readonly CardId[]): string =>
  cs.map(cardToString).join(' ');

/** "KD", "10H", "2D" gibi kısa kodu CardId'ye çevirir. */
export function cardFromString(s: string): CardId {
  const t = s.trim().toUpperCase();
  const suit = t.slice(-1) as Suit;
  const rank = LABEL_RANKS[t.slice(0, -1)];
  if (rank === undefined || !SUITS.includes(suit)) {
    throw new Error(`Geçersiz kart kodu: ${s}`);
  }
  return cardOf(rank, suit);
}

/** "KD KH 2D 3C" → CardId[] */
export const parseCards = (s: string): CardId[] =>
  s.split(/\s+/).filter(Boolean).map(cardFromString);

export const fullDeck = (): CardId[] => Array.from({ length: 52 }, (_, i) => i);

/* ------------------------------------------------------------------ *
 * Sıralama ve gruplama
 * ------------------------------------------------------------------ */

/**
 * Eli renge göre bloklar, blok içinde büyükten küçüğe dizer.
 * Sıra her elde ve her alışverişten sonra AYNI (bkz. `DISPLAY_SUITS`).
 */
export function sortHand(hand: readonly CardId[]): CardId[] {
  return [...hand].sort(
    (a, b) =>
      DISPLAY_SUITS.indexOf(suitOf(a)) - DISPLAY_SUITS.indexOf(suitOf(b)) ||
      rankOf(b) - rankOf(a),
  );
}

/** Renge göre gruplar. Anahtar yoksa o renkten kart yok demek. */
export function groupBySuit(hand: readonly CardId[]): Map<Suit, CardId[]> {
  const g = new Map<Suit, CardId[]>();
  for (const c of [...hand].sort((a, b) => rankOf(a) - rankOf(b))) {
    const s = suitOf(c);
    const list = g.get(s);
    if (list) list.push(c);
    else g.set(s, [c]);
  }
  return g;
}

/** Elin belirli renkteki kartları, KÜÇÜKTEN BÜYÜĞE. */
export const cardsOfSuit = (hand: readonly CardId[], suit: Suit): CardId[] =>
  hand.filter((c) => suitOf(c) === suit).sort((a, b) => rankOf(a) - rankOf(b));

/** Rütbeye göre artan sıralama — "en düşük n kartı" gibi işler için. */
export const byRankAsc = (hand: readonly CardId[]): CardId[] =>
  [...hand].sort((a, b) => rankOf(a) - rankOf(b));

export const lowestCards = (hand: readonly CardId[], n: number): CardId[] =>
  byRankAsc(hand).slice(0, n);

export const highestCards = (hand: readonly CardId[], n: number): CardId[] =>
  byRankAsc(hand).slice(-n);

/* ------------------------------------------------------------------ *
 * Deterministik karıştırma
 *
 * Dağıtım seed'den üretilir; seed sunucuda saklanır. Bu sayede bir el
 * birebir yeniden oynatılabilir (replay, hile denetimi, bug tekrarı,
 * bot testlerinde tekrarlanabilirlik).
 * ------------------------------------------------------------------ */

export function makeRng(seed: string): () => number {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface DealtCards {
  /** Koltuk başına 16 kart. */
  hands: CardId[][];
  /** Yerdeki 4 kapalı kart — 8'ci gömdükten SONRA eline alıyor. */
  kitty: CardId[];
}

/** 3 oyuncuya 16'şar kart, yere 4. */
export function dealHands(seed: string): DealtCards {
  const rng = makeRng(seed);
  const deck = fullDeck();
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [deck[i], deck[j]] = [deck[j]!, deck[i]!];
  }
  return {
    hands: [0, 1, 2].map((s) => sortHand(deck.slice(s * 16, s * 16 + 16))),
    kitty: deck.slice(48),
  };
}
